import React from 'react'
import { assembleFinalEpisodeForPodcast, startBulkSynthesisForPodcast } from '../../../../api/podcasts'
import { useEditorStore } from '../../state/editor-store'
import { PodcastStatusEnum } from '../../../../types/enums'
import { track } from '../../../../lib/analytics/posthog'
import { sanitizeFilename } from '../../../../lib/file-download'
import { useToast } from '../../../../components/ui/toast-provider'
import { useQueryClient } from '@tanstack/react-query'
import { PODCAST_QUERY_KEYS } from '../../../../api/podcasts'

export const useEditorActions = (podcastId?: string) => {
  const [actionLoading, setActionLoading] = React.useState<'synth' | 'assemble' | null>(null)
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  const handleBulkSynthesis = React.useCallback(async () => {
    if (!podcastId) return
    try {
      useEditorStore.getState().setActivity({ phase: 'synthesis', message: 'Preparing audio generation…', step: 'starting' })
      setActionLoading('synth')
      // Track: started audio generation
      track('podcast_audio_generation_started')
      await startBulkSynthesisForPodcast(podcastId)
    } finally {
      setActionLoading(null)
      // Do not clear activity here; SignalR will drive progress and completion
    }
  }, [podcastId])
  
  const handleAssembleFinal = React.useCallback(async () => {
    if (!podcastId) return
    try {
      setActionLoading('assemble')
      
      // Check if podcast already has an assembled audio file
      const currentPodcast = useEditorStore.getState().podcast
      let audioFileUrl = currentPodcast?.audioFileUrl

      if (!audioFileUrl) {
        // Only assemble if no audio file exists
        useEditorStore.getState().setActivity({ phase: 'assemble', message: 'Assembling final episode…', step: 'starting' })
        const result = await assembleFinalEpisodeForPodcast(podcastId)
        audioFileUrl = result.audioFileUrl
        // Immediately reflect assembled URL in local store to unlock UI (e.g., Play button)
        if (audioFileUrl) {
          useEditorStore.setState((s) => {
            if (s.podcast) s.podcast.audioFileUrl = audioFileUrl!
          })
        }
        // Best-effort: keep server/client in sync
        queryClient.invalidateQueries({ queryKey: PODCAST_QUERY_KEYS.detail(podcastId) })
      }

      // Do not auto-download; just inform the user that audio generation is complete
      if (audioFileUrl) {
        const rawTitle = useEditorStore.getState().podcast?.title?.trim() || 'episode'
        const safeTitle = sanitizeFilename(rawTitle)
        // Optional analytics: track that assembly completed
        track('podcast_audio_ready', {
          podcast_id: podcastId,
          podcast_title: safeTitle,
          format: 'mp3'
        })
        addToast({
          title: 'Your episode is ready',
          description: 'Hit Play to preview now, or download it anytime from the player.',
          variant: 'success'
        })
      }
    } finally {
      setActionLoading(null)
      // Clear activity in case backend does not push assemble progress events
      useEditorStore.getState().clearActivity()
    }
  }, [podcastId, addToast, queryClient])

  // Removed per-line regenerate handler and associated state updates

  const hasAllLineAudio = React.useCallback(() => {
    const s = useEditorStore.getState()
    const lines = s.podcast?.podcastLines || []
    // Only check lines that haven't failed - failed lines should not block assembly
    const validLines = lines.filter(l => l.status !== PodcastStatusEnum.Failed)
    return validLines.length > 0 && validLines.every(l => !!l.audioFileUrl && l.audioFileUrl.trim().length > 0)
  }, [])

  const waitForSynthesisCompletion = React.useCallback(async (timeoutMs = 15 * 60 * 1000): Promise<boolean> => {
    // Resolve when activity phase is no longer synthesis and all lines have audio
    const start = Date.now()
    return await new Promise((resolve) => {
      const check = () => {
        const { activity } = useEditorStore.getState()
        const done = activity?.phase !== 'synthesis' && hasAllLineAudio()
        const timedOut = Date.now() - start > timeoutMs
        if (done || timedOut) {
          unsub()
          resolve(done)
        }
      }
      const unsub = useEditorStore.subscribe(() => check())
      // initial check
      check()
    })
  }, [hasAllLineAudio])

  const smartExport = React.useCallback(async () => {
    if (!podcastId) return

    if (!hasAllLineAudio()) {
      await handleBulkSynthesis()
      // Wait for synthesis to finish and lines to update via SignalR
      const ok = await waitForSynthesisCompletion()
      if (!ok) return
    }
    await handleAssembleFinal()
  }, [podcastId, handleBulkSynthesis, handleAssembleFinal, hasAllLineAudio, waitForSynthesisCompletion])

  return {
    actionLoading,
    handleBulkSynthesis,
    handleAssembleFinal,
    smartExport,
  }
}